import React, {useEffect, useRef, useState} from 'react';
import '../../polyfill/urlsearchparams.es5';
import {Stickyfill} from '../../polyfill/stickyfill.es6.js';
import Device from '@core/scripts/components/device';
import Filter from './Filter.jsx';
import Ordering from './Ordering.jsx';
import useEffectOnUpdate from '../helper/hooks-helper';
import FilterGroupsSmall from './FilterGroups.jsx';
import {Constants} from '../view-constants';
import {useDispatch, useSelector} from 'react-redux';
import {selectLoadingbarStatus} from '../LoaderSlice.jsx';
import {closeFilter, selectFilterStatus} from '../FilterSlice.jsx';
import FilterTokens from "./FilterTokens.jsx";
import {useBreakpointUp} from "bonprix-design-system";

export default function FilterOrderingBar({
    filterContent,
    landmarkContent,
    ordering,
    filterTokens,
    showOnlyTokens,
    filterSubmit,
}) {
    const filterOpened = useSelector(selectFilterStatus).opened;
    const contentLoading = useSelector(selectLoadingbarStatus).status;
    const breakpointLUp = useBreakpointUp('l');
    const [isSticky, setIsSticky] = useState(false);
    const dispatch = useDispatch();

    const isApp = Device.isApp();
    const stickyChangeDisabled = useRef(false);
    const scrollSpy = useRef(window.scrollY);
    const rootMargin = useRef(
        breakpointLUp || isApp ? Constants.ROOT_MARGIN_APP : Constants.ROOT_MARGIN_SMALL,
    );
    const stickyContainer = useRef(null);
    const stickyFilterLine = useRef(null);
    const stickyFilterLineObserver = useRef();

    useEffect(() => {
        stickyFilterLineObserver.current = new IntersectionObserver(([entry]) => {
            if (entry.intersectionRatio === 0) {
                setTimeout(() => {
                    stickyChangeDisabled.current = false;
                    stickyFilterLineObserver.current.unobserve(stickyFilterLine.current);
                }, 50);
            }
        });
    }, []);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        getScrollBarWidth();
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [isSticky]);

    useEffect(() => {
        rootMargin.current =
            breakpointLUp || isApp ? Constants.ROOT_MARGIN_APP : Constants.ROOT_MARGIN_SMALL;
    }, [breakpointLUp]);

    useEffectOnUpdate(() => {
        // close filter accordion after content load
        if (filterOpened && !contentLoading) {
            dispatch(closeFilter());
        }
    }, [contentLoading]);

    const sticky = isSticky ? '-is-sticky' : '';
    const stickyDisabled = showOnlyTokens ? '-sticky-disabled' : '';
    const app = isApp ? '-is-app' : '';
    const stickyContainerClasses = `article-overview__filter-order ${sticky} ${stickyDisabled} ${app}`;

    return (
        <>
            <div className="sticky-filter-line" ref={stickyFilterLine}></div>

            <div className={stickyContainerClasses} ref={stickyContainer}>
                {!showOnlyTokens ? (
                    <>
                        <Filter
                            filterContent={filterContent}
                            landmarkContent={landmarkContent}
                            filterSubmit={(clickedFilters) => filterSubmit(clickedFilters, isSticky)}
                            isSticky={isSticky}
                        />
                        <Ordering ordering={ordering} isSticky={isSticky} />
                    </>
                ) : (
                    <></>
                )}

                {breakpointLUp && (
                    <FilterTokens
                        filterTokens={filterTokens}
                        isSticky={isSticky}
                        shopIdFilterGroup={filterContent.shopIdFilterGroup}
                    />
                )}

                {sticky ? <div className="article-overview__sticky-separator"></div> : <></>}
            </div>

            {!breakpointLUp && filterOpened && (
                <FilterGroupsSmall
                    hits={filterContent.hits}
                    filterGroups={filterContent.filterGroups}
                    isSticky={isSticky}
                    shopIdFilterGroup={filterContent.shopIdFilterGroup}
                    filterSubmit={(clickedFilters) => filterSubmit(clickedFilters, isSticky)}
                />
            )}

            {!breakpointLUp && (
                <FilterTokens
                    filterTokens={filterTokens}
                    isSticky={isSticky}
                    shopIdFilterGroup={filterContent.shopIdFilterGroup}
                />
            )}
        </>
    );

    function handleScroll() {
        if (!stickyChangeDisabled.current) {
            const scrollTopStart = window.scrollY || window.pageYOffset || document.body.scrollTop || 0;
            const down = scrollTopStart >= scrollSpy.current;
            const topPosition = stickyContainer.current.getBoundingClientRect().top;
            if (down && !isSticky && topPosition < rootMargin.current) {
                Stickyfill.addOne(stickyContainer.current);
                stickyChangeDisabled.current = true;
                stickyFilterLineObserver.current.observe(stickyFilterLine.current);
                setIsSticky(true);
            } else if (!down && isSticky && topPosition > rootMargin.current) {
                Stickyfill.removeOne(stickyContainer.current);
                stickyChangeDisabled.current = true;
                stickyFilterLineObserver.current.observe(stickyFilterLine.current);
                setIsSticky(false);
            }
        }
        const scrollTopEnd = window.scrollY || window.pageYOffset || document.body.scrollTop || 0;
        scrollSpy.current = scrollTopEnd;
    }

    function getScrollBarWidth() {
        if (window.innerWidth > document.body.clientWidth) {
            stickyContainer.current.setAttribute(
                'style',
                '--scroll-bar-width: ' + (window.innerWidth - document.body.clientWidth) + 'px',
            );
        } else {
            stickyContainer.current.setAttribute('style', '--scroll-bar-width: 0px');
        }
    }
}
